// PatientContext.js - Expanded with Pain Assessment Data
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useClinicalHistoryData } from "../../hooks/queries/useClinicalHistoryData";
import { useHeightsData } from "../../hooks/queries/useHeightsData";
import { usePainAssessmentData } from "../../hooks/queries/usePainAssessmentData";
import { usePatientConditionsData } from "../../hooks/queries/usePatientConditionsData";
import { usePatientData } from "../../hooks/queries/usePatientData";
import { usePatientDigitalTwins } from "../../hooks/queries/usePatientDigitalTwins";
import { usePatientNotesData } from "../../hooks/queries/usePatientNotesData";
import { usePatientReports } from "../../hooks/queries/usePatientReports";
import { useQualityOfLifeIndicatorsData } from "../../hooks/queries/useQualityOfLifeIndicatorsData";
import { useWeightsData } from "../../hooks/queries/useWeightsData";
import { usePatientImages } from "../../hooks/usePatientImages";
import { useRouteState } from "../../hooks/useRouteState";
import { updatePatient } from "../../services/PatientService";

const PatientContext = createContext();

export function PatientProvider({ children }) {
  const { params } = useRouteState();
  const [operationPatientId, setOperationPatientId] = useState(null);

  const {
    patientData,
    loading: patientLoading,
    error: patientError,
    getPersonalData,
    formatPersonalData,
    refetch: refetchPatientData,
  } = usePatientData(params.patientId);

  const {
    patientImages,
    fetchPatientImages,

    addPatientImages,
    filterPatientImagesByDate,
    searchPatientImages,
    imagesLoading,
    imagesError,
  } = usePatientImages(params.patientId);

  const {
    patientDigitalTwinIds,
    fetchPatientDigitalTwinIds,
    digitalTwinsIdsLoading,
    digitalTwinsIdsError,
  } = usePatientDigitalTwins(params.patientId);

  // Add pain assessment data
  const {
    painAssessments,
    assessmentsByDate,
    latestAssessment,
    loading: painAssessmentLoading,
    error: painAssessmentError,
    refreshPainAssessments,
  } = usePainAssessmentData(patientData?.pain_assessments);

  // Add weights data
  const {
    weights,
    weightsByDate,
    latestWeight,
    currentWeightValue,
    loading: weightsLoading,
    error: weightsError,
    refreshWeights,
  } = useWeightsData(patientData?.weights);

  // Add heights data
  const {
    heights,
    heightsByDate,
    latestHeight,
    currentHeightValue,
    loading: heightsLoading,
    error: heightsError,
    refreshHeights,
  } = useHeightsData(patientData?.heights);

  // Add patient notes data
  const {
    patientNotes,
    notesByDate,
    latestNote,
    loading: notesLoading,
    error: notesError,
    refreshNotes,
  } = usePatientNotesData(patientData?.patient_notes);

  // Add patient conditions data
  const {
    patientConditions,
    conditionsByDate,
    latestConditions,
    currentConditions,
    loading: conditionsLoading,
    error: conditionsError,
    refreshConditions,
  } = usePatientConditionsData(patientData?.patient_conditions);

  // Add quality of life indicator data
  const {
    qualityOfLifeIndicators,
    indicatorsByDate: qualityOfLifeIndicatorsByDate,
    latestQualityOfLifeIndicator,
    loading: qualityOfLifeIndicatorsLoading,
    error: qualityOfLifeIndicatorsError,
    refreshQualityOfLifeIndicators,
  } = useQualityOfLifeIndicatorsData(patientData?.quality_of_life_indicators);

  // Add clinical history data
  const {
    clinicalHistories,
    historiesByDate,
    latestHistory,
    loading: clinicalHistoriesLoading,
    error: clinicalHistoriesError,
    refreshClinicalHistories,
  } = useClinicalHistoryData(patientData?.clinical_histories);

  // Add reports data
  const {
    reports,
    loading: reportsLoading,
    error: reportsError,
    refetch: refreshReports,
  } = usePatientReports(params.patientId);

  useEffect(() => {
    if (!params.patientId) return;
    updatePatient(
      { last_access: new Date().toISOString() },
      params.patientId
    ).catch(() => {});
  }, [params.patientId]);

  const updatePatientData = useCallback(
    async updatedData => {
      if (!params.patientId) {
        throw new Error("No patient ID provided for update");
      }

      try {
        await updatePatient(updatedData, params.patientId);
        // CHANGE: Call refetch directly instead of trigger
        await refetchPatientData(); // ADD await here
      } catch (error) {
        throw error; // Re-throw the error
      }
    },
    [params.patientId, refetchPatientData]
  );

  // CHANGE: Call refetch directly and include pain assessments
  const refreshPatientContext = useCallback(async () => {
    try {
      await refetchPatientData();
      await refreshPainAssessments();
      await refreshWeights();
      await refreshHeights();
      await refreshNotes();
      await refreshConditions();
      await refreshQualityOfLifeIndicators();
      await refreshClinicalHistories();
    } catch (error) {}
  }, [
    refetchPatientData,
    refreshPainAssessments,
    refreshWeights,
    refreshHeights,
    refreshNotes,
    refreshConditions,
    refreshQualityOfLifeIndicators,
    refreshClinicalHistories,
  ]);

  const value = {
    patientId: params.patientId,
    operationPatientId,
    setOperationPatientId,
    patientData,
    patientLoading,
    patientError,
    getPersonalData,
    updatePatientData,
    patientImages,
    fetchPatientImages,
    addPatientImages,
    filterPatientImagesByDate,
    searchPatientImages,
    imagesLoading,
    imagesError,

    formatPersonalData,
    refreshPatientContext,
    patientDigitalTwinIds,
    fetchPatientDigitalTwinIds,
    digitalTwinsIdsLoading,
    digitalTwinsIdsError,

    painAssessments,
    assessmentsByDate,
    latestAssessment,
    painAssessmentLoading,
    painAssessmentError,
    refreshPainAssessments,

    weights,
    weightsByDate,
    latestWeight,
    currentWeightValue,
    weightsLoading,
    weightsError,
    refreshWeights,

    heights,
    heightsByDate,
    latestHeight,
    currentHeightValue,
    heightsLoading,
    heightsError,
    refreshHeights,

    patientNotes,
    notesByDate,
    latestNote,
    notesLoading,
    notesError,
    refreshNotes,

    patientConditions,
    conditionsByDate,
    latestConditions,
    currentConditions,
    conditionsLoading,
    conditionsError,
    refreshConditions,
    qualityOfLifeIndicators,
    qualityOfLifeIndicatorsByDate,
    latestQualityOfLifeIndicator,
    qualityOfLifeIndicatorsLoading,
    qualityOfLifeIndicatorsError,
    refreshQualityOfLifeIndicators,
    clinicalHistories,
    historiesByDate,
    latestHistory,
    clinicalHistoriesLoading,
    clinicalHistoriesError,
    refreshClinicalHistories,
    reports,
    reportsLoading,
    reportsError,
    refreshReports,
  };

  return (
    <PatientContext.Provider value={value}>{children}</PatientContext.Provider>
  );
}

export function usePatientContext() {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error("usePatientContext must be used within a PatientProvider");
  }
  return context;
}
